/**
 * Two-slot file requirements panel for the Wholesale Workspace.
 *
 * Mirrors the Daily Photo List FileRequirementsPanel layout:
 *   - Collapsible outer panel with chevron + summary in the header
 *   - One shared drop zone at the top of the panel body
 *   - REQUIRED section heading
 *   - Flat list rows: status icon + label + status text + optional delete
 *
 * The drop zone is the upload affordance — drop any file there and the
 * server detects vendor from headers (Adesa XLSX vs Tekion CSV) and
 * routes it to the right slot. Rows themselves are status-only.
 *
 * Auto-expand rules: expanded by default while a slot is missing,
 * auto-collapses when both Adesa + Tekion are populated, re-expands if a
 * populated slot transitions empty.
 */
import React, { useEffect, useRef, useState } from "react"
import {
  ChevronDown,
  ChevronRight,
  FolderUp,
  Loader2,
  XCircle,
} from "lucide-react"

import {
  WholesaleFileSummary,
  WholesaleSession,
  WholesaleVendor,
} from "../../api/wholesale"
import { THEME } from "../../config/theme"
import { useWholesale } from "../../context/WholesaleContext"
import { formatAge } from "../../utils/formatAge"

interface Props {
  session: WholesaleSession | null
}

interface SlotDef {
  vendor: WholesaleVendor
  label: string
}

const SLOTS: SlotDef[] = [
  { vendor: "adesa",  label: "Adesa Cleared List" },
  { vendor: "tekion", label: "Tekion Inventory" },
]

interface UploadingState {
  filename: string
  status: "uploading" | "error"
  error?: string
}

export default function FileRequirementsPanel({ session }: Props) {
  const filledCount = (session?.adesa ? 1 : 0) + (session?.tekion ? 1 : 0)
  const allReady = filledCount === SLOTS.length
  const missing = SLOTS.length - filledCount

  const [expanded, setExpanded] = useState(!allReady)
  const lastAllReadyRef = useRef(allReady)
  useEffect(() => {
    if (lastAllReadyRef.current !== allReady) {
      lastAllReadyRef.current = allReady
      setExpanded(!allReady)
    }
  }, [allReady])

  // In-flight uploads (one entry per active upload). Each completes by
  // disappearing from this list; populated state then comes from `session`.
  const [uploading, setUploading] = useState<UploadingState[]>([])

  return (
    <div
      style={{
        border: `1px solid ${THEME.colors.silver}`,
        borderRadius: THEME.borderRadius.md,
        backgroundColor: THEME.colors.neutral.light.surface,
        overflow: "hidden",
        fontFamily: THEME.fonts.primary,
      }}
    >
      <Header
        expanded={expanded}
        missing={missing}
        allReady={allReady}
        onToggle={() => setExpanded((v) => !v)}
      />

      {expanded && (
        <div style={{ padding: `0 ${THEME.spacing.md} ${THEME.spacing.md}` }}>
          <SharedDropZone uploading={uploading} setUploading={setUploading} />

          <SectionHeading>Required</SectionHeading>

          <div>
            {SLOTS.map((slot) => (
              <SlotRow
                key={slot.vendor}
                slot={slot}
                file={session ? session[slot.vendor] : null}
                uploading={uploading.find((u) => guessVendorFromFilename(u.filename) === slot.vendor)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

interface HeaderProps {
  expanded: boolean
  missing: number
  allReady: boolean
  onToggle: () => void
}

function Header({ expanded, missing, allReady, onToggle }: HeaderProps) {
  const summaryText = allReady
    ? "all ready"
    : `${missing} required file${missing === 1 ? "" : "s"} missing`
  const summaryColor = allReady
    ? THEME.colors.semantic.success
    : THEME.colors.semantic.warning

  return (
    <div
      onClick={onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        gap: THEME.spacing.sm,
        padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
        cursor: "pointer",
        userSelect: "none",
        fontSize: 13,
        fontWeight: 600,
        color: THEME.colors.neutral.light.textPrimary,
      }}
    >
      {expanded ? (
        <ChevronDown size={14} strokeWidth={2} style={{ color: THEME.colors.neutral.light.textSecondary }} />
      ) : (
        <ChevronRight size={14} strokeWidth={2} style={{ color: THEME.colors.neutral.light.textSecondary }} />
      )}
      File Requirements
      <span style={{ fontWeight: 400, fontSize: 12, marginLeft: 4, color: summaryColor }}>
        — {summaryText}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared drop zone
// ---------------------------------------------------------------------------

interface DropZoneProps {
  uploading: UploadingState[]
  setUploading: React.Dispatch<React.SetStateAction<UploadingState[]>>
}

function SharedDropZone({ uploading, setUploading }: DropZoneProps) {
  const { uploadFile } = useWholesale()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [dragOver, setDragOver] = useState(false)

  const beginUploads = async (files: File[]) => {
    for (const incoming of files) {
      setUploading((prev) => [...prev, { filename: incoming.name, status: "uploading" }])
      try {
        await uploadFile(incoming)
        setUploading((prev) => prev.filter((u) => u.filename !== incoming.name))
      } catch (err) {
        const message = err instanceof Error ? err.message : "Upload failed"
        setUploading((prev) =>
          prev.map((u) =>
            u.filename === incoming.name
              ? { ...u, status: "error", error: message }
              : u,
          ),
        )
        // Clear the error entry after a delay so the user sees it briefly.
        setTimeout(() => {
          setUploading((prev) => prev.filter((u) => u.filename !== incoming.name))
        }, 4000)
      }
    }
  }

  const handleClick = () => inputRef.current?.click()

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const list = event.target.files
    if (list && list.length > 0) beginUploads(Array.from(list))
    event.target.value = ""
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragOver(false)
    const list = event.dataTransfer.files
    if (list && list.length > 0) beginUploads(Array.from(list))
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragOver(true)
  }

  return (
    <div>
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragOver(false)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: THEME.spacing.sm,
          padding: `${THEME.spacing.md} ${THEME.spacing.md}`,
          border: `1.5px dashed ${dragOver ? THEME.colors.navy : THEME.colors.silver}`,
          borderRadius: THEME.borderRadius.md,
          background: dragOver
            ? "rgba(13, 26, 66, 0.06)"
            : THEME.colors.neutral.light.background,
          cursor: "pointer",
          fontSize: 13,
          color: THEME.colors.neutral.light.textSecondary,
          transition: "background 120ms ease-out, border-color 120ms ease-out",
        }}
      >
        <FolderUp size={16} strokeWidth={1.75} />
        <span>Drag files here or click to browse</span>
      </div>

      {uploading.length > 0 && (
        <div style={{ marginTop: THEME.spacing.sm, display: "flex", flexDirection: "column", gap: 4 }}>
          {uploading.map((u) => (
            <div
              key={u.filename}
              style={{
                display: "flex",
                alignItems: "center",
                gap: THEME.spacing.sm,
                fontSize: 12,
                color: u.status === "error"
                  ? THEME.colors.semantic.danger
                  : THEME.colors.neutral.light.textSecondary,
              }}
            >
              {u.status === "uploading" ? (
                <Loader2 size={14} className="invi-spin" style={{ color: THEME.colors.navy }} />
              ) : (
                <XCircle size={14} color={THEME.colors.semantic.danger} />
              )}
              <span style={{ fontWeight: 500 }}>{u.filename}</span>
              {u.status === "uploading" && <span>uploading…</span>}
              {u.status === "error" && <span>{u.error ?? "upload failed"}</span>}
            </div>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        multiple
        style={{ display: "none" }}
        onChange={handleFileInput}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// REQUIRED section + per-slot rows
// ---------------------------------------------------------------------------

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: THEME.typography.caption.size,
        fontWeight: THEME.typography.caption.weight,
        letterSpacing: THEME.typography.caption.tracking,
        textTransform: "uppercase",
        color: THEME.colors.neutral.light.textSecondary,
        marginTop: THEME.spacing.md,
        marginBottom: THEME.spacing.sm,
      }}
    >
      {children}
    </div>
  )
}

interface SlotRowProps {
  slot: SlotDef
  file: WholesaleFileSummary | null
  uploading: UploadingState | undefined
}

function SlotRow({ slot, file, uploading }: SlotRowProps) {
  const populated = file !== null && !uploading
  const isUploading = !!uploading
  const isMissing = !populated && !isUploading

  // Mirrors the photo list FileRow: glyph in a fixed-width span, fixed-width
  // label column, then the status text fills the rest. Delete lives in the
  // Active Vendor Files panel below, not here.
  const { symbol, color } = pickGlyph({ uploading: isUploading, populated })

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: THEME.spacing.sm,
        padding: "3px 0",
        minHeight: 22,
        fontSize: 13,
      }}
    >
      <span
        className={isUploading ? "invi-spin" : undefined}
        style={{
          width: 18,
          textAlign: "center",
          flexShrink: 0,
          color,
          fontWeight: 600,
          fontSize: 14,
        }}
      >
        {symbol}
      </span>
      <span
        style={{
          width: 200,
          flexShrink: 0,
          fontWeight: 500,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      >
        {slot.label}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: isMissing
            ? color
            : THEME.colors.neutral.light.textSecondary,
          fontStyle: isMissing ? "italic" : "normal",
        }}
      >
        {renderDetail({ uploading, file })}
      </span>
    </div>
  )
}

function pickGlyph({
  uploading,
  populated,
}: {
  uploading: boolean
  populated: boolean
}): { symbol: string; color: string } {
  if (uploading) return { symbol: "↻", color: THEME.colors.navy }
  if (populated) return { symbol: "✓", color: THEME.colors.semantic.success }
  return { symbol: "○", color: THEME.colors.neutral.light.textSecondary }
}

interface DetailArgs {
  uploading: UploadingState | undefined
  file: WholesaleFileSummary | null
}

function renderDetail({ uploading, file }: DetailArgs): string {
  if (uploading) {
    return uploading.status === "error"
      ? (uploading.error ?? "upload failed")
      : "uploading…"
  }
  if (file) {
    const ageLabel = ageFromIso(file.uploaded_at)
    const rowsLabel = `${file.row_count.toLocaleString()} rows`
    return `${file.filename} — ${rowsLabel}${ageLabel ? ` • ${ageLabel}` : ""}`
  }
  return "not uploaded"
}

function ageFromIso(uploadedAt: string | null): string {
  if (!uploadedAt) return ""
  const ms = Date.now() - new Date(uploadedAt).getTime()
  if (Number.isNaN(ms) || ms < 0) return ""
  return formatAge(Math.floor(ms / 1000))
}

// Best-effort guess of which slot an in-flight upload will land in.
// Backend is the authoritative detector; this is purely so the row in the
// list shows a spinner near the right slot while the request is in flight.
function guessVendorFromFilename(filename: string): WholesaleVendor | null {
  const lower = filename.toLowerCase()
  if (lower.endsWith(".xlsx") || lower.endsWith(".xls")) return "adesa"
  if (lower.endsWith(".csv")) {
    if (lower.includes("adesa")) return "adesa"
    return "tekion"
  }
  return null
}
